import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import universalDataService from '../services/universalDataService';
import { ConfirmPanel, Field, Icon, Panel, formatDate } from './ui';
import {
  PORTABLE_NAME, availableIn, buildDispensing, buildPurchase, computeAvgPrice, computeLevels, dispensingError, emptyDispensing,
  emptyPurchase, fuelEvents, fuelTotals, normalizeFuel, purchaseError, replaceOrAdd, utilajReport,
} from './fuelLogic';
import './Motorina.css';

const liters = (value) => `${(Number(value) || 0).toLocaleString('ro-RO', { maximumFractionDigits: 1 })} L`;
const lei = (value) => `${Math.round(value || 0).toLocaleString('ro-RO')} lei`;
const price = (value) => `${(value || 0).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei/L`;
const decimalText = (value) => value.replace(',', '.').replace(/[^0-9.]/g, '');
const typed = (value) => parseFloat(value) || 0;
const VIEWS = [{ id: 'rezumat', label: 'Rezumat' }, { id: 'achizitii', label: 'Achiziții' }, { id: 'iesiri', label: 'Ieșiri' }, { id: 'raport', label: 'Raport' }];

const uniqueTexts = (list, key) => [...new Set((list || []).map((item) => item[key]).filter(Boolean))];

// ---------- Forms ----------

const PurchasePanel = ({ record, suppliers, saving, onSave, onDelete, onClose }) => {
  const [form, setForm] = useState(() => (record ? { ...record, liters: String(record.liters), pricePerLiter: String(record.pricePerLiter) } : emptyPurchase()));
  const [error, setError] = useState('');
  const total = typed(form.liters) * typed(form.pricePerLiter);

  const submit = (event) => {
    event.preventDefault();
    const problem = purchaseError(form);
    setError(problem);
    if (!problem) onSave(form);
  };

  return (
    <Panel title={record ? 'Modifică achiziția' : 'Achiziție motorină'} onClose={onClose} onSubmit={submit}
      footer={(
        <>
          {record && <button type="button" className="pm-btn-soft pm-danger" onClick={onDelete}>Șterge</button>}
          <button type="submit" className="pm-btn-accent" disabled={saving}>{saving ? 'Se salvează…' : 'Salvează achiziția'}</button>
        </>
      )}>
      {error && <p className="pm-error">{error}</p>}
      <div className="mo-big">
        <Field label="Litri"><input inputMode="decimal" placeholder="5000" value={form.liters} onChange={(e) => setForm({ ...form, liters: decimalText(e.target.value) })} /></Field>
      </div>
      <div className="pm-two">
        <Field label="Preț (lei/litru)"><input inputMode="decimal" placeholder="7.50" value={form.pricePerLiter} onChange={(e) => setForm({ ...form, pricePerLiter: decimalText(e.target.value) })} /></Field>
        <Field label="Data"><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
      </div>
      {total > 0 && <p className="mo-tip">Total factură {lei(total)}</p>}
      <Field label="Furnizor">
        <input list="mo-suppliers" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
        <datalist id="mo-suppliers">{suppliers.map((name) => <option key={name} value={name} />)}</datalist>
      </Field>
      <Field label="Note"><input placeholder="Opțional" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Field>
    </Panel>
  );
};

const DispensingPanel = ({ record, data, utilaje, drivers, avgPrice, saving, onSave, onDelete, onClose }) => {
  const [form, setForm] = useState(() => (record ? { ...record, liters: String(record.liters) } : emptyDispensing()));
  const [error, setError] = useState('');
  const portableCapacity = data.portableTankCapacity || 1000;
  const { portableLevel } = computeLevels(data);
  const toPortable = form.toType === 'portable';
  const portableAfter = portableLevel - (record?.toType === 'portable' ? record.liters : 0) + typed(form.liters);

  const chooseTank = (fromTank) => setForm({ ...form, fromTank, toType: 'utilaj', toId: '', toName: '' });
  const submit = (event) => {
    event.preventDefault();
    const problem = dispensingError(form, data, record);
    setError(problem);
    if (!problem) onSave(form);
  };

  return (
    <Panel title={record ? 'Modifică ieșirea' : 'Ieșire combustibil'} onClose={onClose} onSubmit={submit}
      footer={(
        <>
          {record && <button type="button" className="pm-btn-soft pm-danger" onClick={onDelete}>Șterge</button>}
          <button type="submit" className="pm-btn-accent" disabled={saving}>{saving ? 'Se salvează…' : 'Salvează ieșirea'}</button>
        </>
      )}>
      {error && <p className="pm-error">{error}</p>}
      <div>
        <p className="mo-label">Din rezervor</p>
        <div className="mo-choice is-dark" role="group" aria-label="Din rezervor">
          {[['main', 'Principal'], ['portable', 'Portabil']].map(([id, label]) => (
            <button key={id} type="button" className={form.fromTank === id ? 'is-on' : ''} aria-pressed={form.fromTank === id} onClick={() => chooseTank(id)}>
              <strong>{label}</strong><span>{liters(availableIn(data, id, record))} disponibili</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="mo-label">Către</p>
        <div className="mo-choice" role="group" aria-label="Către">
          {utilaje.map((machine) => {
            const on = form.toType === 'utilaj' && String(form.toId) === String(machine.id);
            return (
              <button key={machine.id} type="button" className={on ? 'is-on' : ''} aria-pressed={on}
                onClick={() => setForm({ ...form, toType: 'utilaj', toId: String(machine.id), toName: machine.name })}>
                <strong>{machine.name}</strong>{machine.brand && <span>{machine.brand}</span>}
              </button>
            );
          })}
          {form.fromTank === 'main' && (
            <button type="button" className={toPortable ? 'is-on' : ''} aria-pressed={toPortable}
              onClick={() => setForm({ ...form, toType: 'portable', toId: 'portable', toName: PORTABLE_NAME })}>
              <strong>Rezervor portabil</strong><span>{liters(portableLevel)} curent</span>
            </button>
          )}
        </div>
        {utilaje.length === 0 && <p className="pm-muted">Adaugă utilaje în pagina Utilaje ca să le poți alege aici.</p>}
      </div>
      <div className="mo-big">
        <Field label="Litri"><input inputMode="decimal" placeholder="100" value={form.liters} onChange={(e) => setForm({ ...form, liters: decimalText(e.target.value) })} /></Field>
      </div>
      {typed(form.liters) > 0 && avgPrice > 0 && <p className="mo-tip">Cost estimat {lei(typed(form.liters) * avgPrice)}, la {price(avgPrice)} preț mediu</p>}
      {toPortable && portableAfter > portableCapacity && <p className="pm-note">Rezervorul portabil are {liters(portableCapacity)}. Cu această cantitate ar ajunge la {liters(portableAfter)}.</p>}
      <div className="pm-two">
        <Field label="Șofer">
          <input list="mo-drivers" value={form.driver} onChange={(e) => setForm({ ...form, driver: e.target.value })} />
          <datalist id="mo-drivers">{drivers.map((name) => <option key={name} value={name} />)}</datalist>
        </Field>
        <Field label="Data"><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
      </div>
      <Field label="Note"><input placeholder="Opțional" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Field>
    </Panel>
  );
};

// ---------- Lines ----------

const PurchaseRow = ({ record, onOpen }) => (
  <button type="button" className="pm-row" onClick={onOpen}>
    <div className="pm-row-main">
      <strong>Achiziție{record.supplier ? ` · ${record.supplier}` : ''}</strong>
      <span>{formatDate(record.date)} · {record.pricePerLiter} lei/L · {lei(record.liters * record.pricePerLiter)}</span>
      {record.note && <span>{record.note}</span>}
    </div>
    <span className="pm-row-end mo-in">+{liters(record.liters)}</span>
  </button>
);

const DispensingRow = ({ record, name, avgPrice, onOpen }) => (
  <button type="button" className="pm-row" onClick={onOpen}>
    <div className="pm-row-main">
      <strong>{record.fromTank === 'main' ? 'Principal' : 'Portabil'} → {name}</strong>
      <span>{formatDate(record.date)}{record.driver ? ` · ${record.driver}` : ''}{avgPrice > 0 ? ` · ${lei(record.liters * avgPrice)}` : ''}</span>
      {record.note && <span>{record.note}</span>}
    </div>
    <span className="pm-row-end mo-out">−{liters(record.liters)}</span>
  </button>
);

// ---------- Page ----------

const Motorina = ({ dataService = universalDataService }) => {
  const [data, setData] = useState(() => normalizeFuel(null));
  const [utilaje, setUtilaje] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('rezumat');
  const [form, setForm] = useState(null); // { kind: 'purchase' | 'dispensing', record }
  const [confirm, setConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = (message, type = 'success') => {
    clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), type === 'error' ? 6000 : 3000);
  };
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const load = useCallback(async () => {
    const [fuel, machines] = await Promise.all([dataService.loadMotorina(), dataService.loadUtilaje()]);
    setData(normalizeFuel(fuel));
    setUtilaje(Array.isArray(machines) ? machines : []);
  }, [dataService]);

  useEffect(() => {
    load().catch((error) => console.error('Motorină: load failed', error)).finally(() => setLoading(false));
    // A change from another device arrives while this page is open.
    const reload = () => load().catch((error) => console.error('Motorină: reload failed', error));
    window.addEventListener('cloudSyncComplete', reload);
    return () => window.removeEventListener('cloudSyncComplete', reload);
  }, [load]);

  // Starts from the newest saved copy, so a change from another device is not overwritten.
  const change = async (update) => {
    const next = update(normalizeFuel(await dataService.loadMotorina()));
    await dataService.saveMotorina(next);
    setData(next);
  };

  const { mainLevel, portableLevel } = computeLevels(data);
  const avgPrice = computeAvgPrice(data.purchases);
  const totals = fuelTotals(data);
  const tankCap = data.tankCapacity || 15000;
  const portCap = data.portableTankCapacity || 1000;
  const events = useMemo(() => fuelEvents(data), [data]);
  const report = useMemo(() => utilajReport(data, utilaje), [data, utilaje]);
  const nameOf = (record) => (record.toType === 'utilaj' ? utilaje.find((u) => String(u.id) === String(record.toId))?.name || record.toName : 'Rezervor portabil');
  const percent = (level, capacity) => (capacity > 0 ? Math.min(100, (level / capacity) * 100) : 0);

  const savePurchase = async (values) => {
    setSaving(true);
    const { record } = form;
    try {
      const saved = buildPurchase(values, record?.id);
      await change((current) => ({ ...current, purchases: replaceOrAdd(current.purchases, saved, Boolean(record)) }));
      setForm(null);
      showToast(record ? 'Achiziția a fost modificată.' : 'Achiziția a fost salvată.');
    } catch (error) {
      console.error('Motorină: save failed', error);
      showToast('Nu s-a salvat. Încearcă din nou.', 'error');
    } finally { setSaving(false); }
  };

  const saveDispensing = async (values) => {
    setSaving(true);
    const { record } = form;
    try {
      const saved = buildDispensing(values, record?.id);
      await change((current) => ({ ...current, dispensing: replaceOrAdd(current.dispensing, saved, Boolean(record)) }));
      setForm(null);
      showToast(record ? 'Ieșirea a fost modificată.' : 'Ieșirea a fost salvată.');
    } catch (error) {
      console.error('Motorină: save failed', error);
      showToast('Nu s-a salvat. Încearcă din nou.', 'error');
    } finally { setSaving(false); }
  };

  const askDelete = (kind, record) => setConfirm({
    title: kind === 'purchase' ? 'Ștergi achiziția?' : 'Ștergi ieșirea?',
    text: `${liters(record.liters)} din ${formatDate(record.date)}. Nivelul rezervorului se recalculează. Ștergerea nu se poate anula.`,
    action: 'Șterge',
    onYes: async () => {
      setConfirm(null);
      const key = kind === 'purchase' ? 'purchases' : 'dispensing';
      try {
        await change((current) => ({ ...current, [key]: (current[key] || []).filter((item) => item.id !== record.id) }));
        setForm(null);
        showToast('Înregistrarea a fost ștearsă.');
      } catch (error) {
        console.error('Motorină: delete failed', error);
        showToast('Nu s-a șters. Încearcă din nou.', 'error');
      }
    },
  });

  const sortedPurchases = useMemo(() => [...(data.purchases || [])].sort((a, b) => new Date(b.date) - new Date(a.date)), [data]);
  const sortedDispensing = useMemo(() => [...(data.dispensing || [])].sort((a, b) => new Date(b.date) - new Date(a.date)), [data]);
  const stats = [
    [liters(totals.litersIn), 'Total intrat'], [liters(totals.litersOut), 'Total ieșit'], [avgPrice > 0 ? price(avgPrice) : '—', 'Preț mediu'],
    [(data.purchases || []).length, 'Achiziții'], [(data.dispensing || []).length, 'Ieșiri'], [lei(totals.cost), 'Cost total'],
  ];

  return (
    <div className="pm mo">
      <header className="pm-head"><h1>Motorină</h1></header>

      {loading ? <p className="pm-empty">Se încarcă datele…</p> : (
        <>
          <section className="mo-hero">
            <div className="mo-hero-top"><p>Rezervor principal</p><p>din {liters(tankCap)}</p></div>
            <strong className="mo-hero-big">{liters(mainLevel)}</strong>
            <div className={`mo-bar ${percent(mainLevel, tankCap) <= 15 ? 'is-low' : ''}`}><i style={{ width: `${percent(mainLevel, tankCap)}%` }} /></div>
            {avgPrice > 0 && <p className="mo-hero-sub">Valoare {lei(mainLevel * avgPrice)} · preț mediu {price(avgPrice)}</p>}
            <div className="mo-portable">
              <div>Portabil<strong>{liters(portableLevel)}</strong></div>
              <div>
                <div className={`mo-bar ${percent(portableLevel, portCap) <= 15 ? 'is-low' : ''}`}><i style={{ width: `${percent(portableLevel, portCap)}%` }} /></div>
                <p>din {liters(portCap)}{avgPrice > 0 && portableLevel > 0 ? ` · ${lei(portableLevel * avgPrice)}` : ''}</p>
              </div>
            </div>
          </section>

          <div className="mo-main-actions">
            <button type="button" className="pm-btn-accent" onClick={() => setForm({ kind: 'dispensing', record: null })}><Icon d="M12 19V5M6 11l6-6 6 6" size={18} />Ieșire</button>
            <button type="button" className="pm-btn-plain" onClick={() => setForm({ kind: 'purchase', record: null })}><Icon d="M12 5v14M6 13l6 6 6-6" size={18} />Achiziție</button>
          </div>

          <div className="pm-segments mo-segments" role="group" aria-label="Secțiuni">
            {VIEWS.map((item) => <button key={item.id} type="button" className={view === item.id ? 'is-on' : ''} aria-pressed={view === item.id} onClick={() => setView(item.id)}>{item.label}</button>)}
          </div>

          {view === 'rezumat' && (
            <>
              <div className="mo-stats">{stats.map(([value, label]) => <div key={label}><strong>{value}</strong><span>{label}</span></div>)}</div>
              <h2 className="mo-heading">Activitate recentă</h2>
              <div className="pm-list">
                {events.length === 0 && <p className="pm-empty">Nicio activitate. Adaugă o achiziție sau o ieșire.</p>}
                {events.slice(0, 12).map((event) => (event._type === 'purchase'
                  ? <PurchaseRow key={`p${event.id}`} record={event} onOpen={() => setForm({ kind: 'purchase', record: data.purchases.find((p) => p.id === event.id) })} />
                  : <DispensingRow key={`d${event.id}`} record={event} name={nameOf(event)} avgPrice={avgPrice} onOpen={() => setForm({ kind: 'dispensing', record: data.dispensing.find((d) => d.id === event.id) })} />))}
              </div>
            </>
          )}

          {view === 'achizitii' && (
            <div className="pm-list">
              {sortedPurchases.length === 0 && <p className="pm-empty">Nicio achiziție încă.</p>}
              {sortedPurchases.map((record) => <PurchaseRow key={record.id} record={record} onOpen={() => setForm({ kind: 'purchase', record })} />)}
            </div>
          )}

          {view === 'iesiri' && (
            <div className="pm-list">
              {sortedDispensing.length === 0 && <p className="pm-empty">Nicio ieșire încă.</p>}
              {sortedDispensing.map((record) => <DispensingRow key={record.id} record={record} name={nameOf(record)} avgPrice={avgPrice} onOpen={() => setForm({ kind: 'dispensing', record })} />)}
            </div>
          )}

          {view === 'raport' && (
            <>
              <h2 className="mo-heading">Consum pe utilaj</h2>
              <div className="pm-list">
                {report.length === 0 && <p className="pm-empty">Nicio ieșire pe utilaje încă.</p>}
                {report.map((row) => (
                  <div key={row.key} className="pm-row">
                    <div className="pm-row-main"><strong>{row.name}</strong><span>{lei(row.cost)} cost estimat</span></div>
                    <span className="pm-row-end">{liters(row.liters)}</span>
                  </div>
                ))}
                {report.length > 0 && (
                  <div className="mo-table-total">
                    <span>Total</span>
                    <span>{liters(report.reduce((sum, row) => sum + row.liters, 0))} · {lei(report.reduce((sum, row) => sum + row.cost, 0))}</span>
                  </div>
                )}
              </div>
              {avgPrice > 0 && <p className="pm-note st-hint-top">Preț mediu ponderat: {avgPrice.toFixed(4)} lei/L. Costul estimat folosește acest preț.</p>}
            </>
          )}
        </>
      )}

      {form?.kind === 'purchase' && (
        <PurchasePanel record={form.record} suppliers={uniqueTexts(data.purchases, 'supplier')} saving={saving} onSave={savePurchase}
          onDelete={() => askDelete('purchase', form.record)} onClose={() => setForm(null)} />
      )}
      {form?.kind === 'dispensing' && (
        <DispensingPanel record={form.record} data={data} utilaje={utilaje} drivers={uniqueTexts(data.dispensing, 'driver')} avgPrice={avgPrice} saving={saving}
          onSave={saveDispensing} onDelete={() => askDelete('dispensing', form.record)} onClose={() => setForm(null)} />
      )}
      {confirm && <ConfirmPanel {...confirm} onClose={() => setConfirm(null)} />}
      {toast && <div className={`pm-toast is-${toast.type}`} role="status">{toast.message}</div>}
    </div>
  );
};

export default Motorina;
